import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { STORE_SITE_DEFAULTS, buildStoreSiteContentMap, deepMergeStoreSiteContent, normalizeBrandingNavItems } from '../storeSiteContent';
import { fetchStoreSiteContent, upsertStoreSiteContent, uploadStoreSiteAsset } from '../supabaseClient';
import { PublicSiteHeader } from './PublicSite';
import './PublicSite.css';

const PAGE_LABELS = {
  branding: 'Branding',
  home: 'Home',
  about: 'About',
  contact: 'Contact',
};

const BRANDING_FIELDS = [
  { key: 'logoAlt', label: 'Logo alt text', type: 'text', placeholder: 'Store logo' },
  { key: 'logoWidthDesktop', label: 'Desktop logo width', type: 'number', placeholder: '200' },
  { key: 'logoWidthMobile', label: 'Mobile logo width', type: 'number', placeholder: '150' },
];

const cloneValue = (value) => JSON.parse(JSON.stringify(value));
const getByPath = (obj, path) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const pathFromNavItem = (item) => String(item?.to || '').replace(/^\//, '').trim();

const setByPath = (obj, path, value) => {
  const keys = path.split('.');
  const next = cloneValue(obj);
  let cursor = next;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) { cursor[key] = value; return; }
    cursor[key] = cursor[key] ? cloneValue(cursor[key]) : {};
    cursor = cursor[key];
  });
  return next;
};

// Matches PublicSite.js's DEFAULT_CUSTOM_SECTION_BG — a custom section should look the same
// shade in the editor as it will on the live page.
const DEFAULT_CUSTOM_SECTION_BG = '#ffffff';

// Matches PublicSite.js's getSectionBackground — branding.wrapperEnabled === false means
// every section card goes fully transparent (no wrapper-colored layer at all), same as the
// live page, so the editor previews this toggle accurately instead of always showing the
// wrapper-colored card regardless of the setting.
const getSectionBackground = (cs, branding) => cs.background || (branding?.wrapperEnabled === false ? 'transparent' : (branding?.wrapperColor || DEFAULT_CUSTOM_SECTION_BG));

// ── Color helpers (hex <-> rgb <-> hsv) — shared by the theme-color tint/shade ladder and
// the "More Colors" dialog's picker + RGB/Hex fields. ──
const clamp255 = (n) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const rgbToHex = ({ r, g, b }) => `#${[r, g, b].map((c) => clamp255(c).toString(16).padStart(2, '0')).join('')}`;
const rgbToHsv = ({ r, g, b }) => {
  const rn = r / 255; const gn = g / 255; const bn = b / 255;
  const max = Math.max(rn, gn, bn); const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
};
const hsvToRgb = ({ h, s, v }) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rgb;
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return { r: clamp255((rgb[0] + m) * 255), g: clamp255((rgb[1] + m) * 255), b: clamp255((rgb[2] + m) * 255) };
};
// Mixes a base color toward white (amount > 0, a Word "Lighter X%" tint) or black
// (amount < 0, a "Darker X%" shade) — same idea as Word's theme-color tint/shade ladder.
const mixToward = (hex, amount) => {
  const { r, g, b } = hexToRgb(hex);
  const target = amount > 0 ? 255 : 0;
  const t = Math.abs(amount);
  return rgbToHex({ r: r + (target - r) * t, g: g + (target - g) * t, b: b + (target - b) * t });
};

// Word's "Theme Colors" row is populated from the document's theme — this app doesn't have
// one, so these are Servsolo's own brand colors instead (the same ones already used across
// the editor/live site), each with the same tint/shade ladder Word shows under every theme
// color. TINT_SHADE_STEPS mirrors Word's typical 5-step ladder (two lighter, base, two
// darker) — fewer steps than Word's 6 since this palette has more base colors to begin with.
const THEME_BASE_COLORS = ['#14213d', '#5f6c84', '#0f766e', '#ea580c', '#1ea0ff', '#dc2626'];
const TINT_SHADE_STEPS = [0.6, 0.3, 0, -0.25, -0.5];

// Word's own default "Standard Colors" row — kept verbatim (not site-branded) since these are
// meant to be generic, recognizable colors independent of any theme.
const STANDARD_COLORS = ['#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0'];

const RICH_TEXT_FONTS = ['Manrope', 'Fraunces', 'Arial', 'Calibri', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New'];
// Absolute point sizes (like Word/Office), not relative em — relative units compound when
// the selection already sits inside a previously-applied size span, which is why "Small"
// used to make text shrink further every time it was clicked.
const RICH_TEXT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 54, 60, 66, 72];

// Block-level controls (Align/Spacing/Background) for freeform Text/Button/Chip blocks —
// shown in the field's own floating toolbar (see RichTextField below) only when a field
// opts in via the align/spacing/bgColor props, instead of a permanent settings row sitting
// in the page layout for every block all the time.
const BLOCK_ALIGN_TITLES = { left: 'Align left', center: 'Align center', right: 'Align right', justify: 'Justify' };
// Word-style line-spacing presets — stored as the literal line-height multiplier (not an
// enum) so it maps directly onto CSS, same set of values as Word's own Line Spacing menu.
const LINE_SPACING_OPTIONS = [1, 1.15, 1.5, 2, 2.5, 3];

// Four bars mimicking Word/Office's alignment icons — left/center/right vary each bar's
// width and where it sits on the line; justify is just four full-width bars.
const ALIGN_ICON_WIDTHS = [100, 70, 100, 55];
const AlignIcon = ({ align }) => (
  <span className={`psm-align-icon psm-align-icon-${align}`}>
    {ALIGN_ICON_WIDTHS.map((w, i) => (
      <span key={i} className="psm-align-icon-bar" style={{ width: `${align === 'justify' ? 100 : w}%` }} />
    ))}
  </span>
);

// Keeps the editor canvas laid out at its real 1276px (1220px content + 28px padding each
// side) width at all times — same technique design tools (Webflow, Framer) use for a "device
// preview": the browser always computes wrapping/spacing at the true size, and only the
// *display* is scaled down with a CSS transform to fit whatever space the admin panel's
// sidebar + page-list column actually leave. That guarantees the wrapping matches the live
// page exactly instead of approximately — earlier attempts (CSS min-width, then forcing the
// width directly via JS) tried to make the canvas itself wider, which either didn't take
// effect or overshot into a runaway feedback loop. Scaling sidesteps both: the canvas's own
// width never changes, so there's nothing for it to feed back into.
const useScaledCanvas = (naturalWidth = 1276) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    canvas.style.transformOrigin = 'top left';
    const apply = () => {
      const availableWidth = wrapper.getBoundingClientRect().width;
      if (availableWidth <= 0) return;
      // The canvas's own width was pinned to naturalWidth (1276px) at all times — fine when
      // the wrapper is narrower (it scales down to fit), but in a spacious context (the
      // fullscreen preview window, with no admin sidebar at all) the canvas still only
      // claimed 1276px and sat left-anchored, leaving the rest of the viewport blank
      // instead of filling/centering. Growing it to match availableWidth whenever there's
      // more room keeps scale at a natural 1, with no leftover dead space.
      const canvasWidth = Math.max(naturalWidth, availableWidth);
      canvas.style.width = `${canvasWidth}px`;
      const scale = Math.min(1, availableWidth / canvasWidth);
      // Applying transform:scale(1) is mathematically a no-op, but it still creates a new
      // compositing layer — which can shift sub-pixel rounding by ~1px versus not applying
      // any transform at all. Skipping the transform entirely whenever there's enough room
      // (scale would be exactly 1 anyway, since canvasWidth already equals availableWidth
      // in that case) avoids that, instead of fighting browser-level rounding after the
      // fact.
      canvas.style.transform = scale < 1 ? `scale(${scale})` : 'none';
      const naturalHeight = canvas.getBoundingClientRect().height / scale;
      wrapper.style.height = `${naturalHeight * scale}px`;
    };
    apply();
    // A scrollbar that appears after this first measurement (because the canvas's own
    // height, once rendered, makes the page taller than the viewport) shrinks the wrapper's
    // available width by its own ~15-17px — re-running once more on the next frame, after
    // that scrollbar has had a chance to appear, catches that instead of leaving the canvas
    // sized against a slightly-too-wide pre-scrollbar measurement.
    const raf = window.requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(wrapper);
    ro.observe(canvas);
    window.addEventListener('resize', apply);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', apply);
      ro.disconnect();
    };
  }, [naturalWidth]);
  return { wrapperRef, canvasRef };
};

// A small button-based dropdown (not a native <select>) — every option is a <button> so the
// toolbar's mousedown-preventDefault keeps the text selection alive when picking an option.
// A native <select> would lose that selection the moment focus moves to it.
const RichTextDropdown = ({ label, isOpen, onToggle, children }) => (
  <div className="psm-richtext-dropdown">
    <button type="button" className="psm-richtext-btn psm-richtext-dropdown-trigger" onClick={onToggle}>{label} ▾</button>
    {isOpen && <div className="psm-richtext-dropdown-panel">{children}</div>}
  </div>
);

// Word's "Colors" dialog — an HSV saturation/value square + hue strip, kept in sync with
// plain RGB number inputs and a hex field (typing in any one updates the rest, since they're
// all just different views of the same hsv state). Portaled + centered overlay rather than
// anchored under a trigger, matching how Word's own dialog floats independently of the
// ribbon button that opened it. No gradient option, per request — this is solid color only.
const MoreColorsDialog = ({ initialColor, onPick, onClose }) => {
  const [hsv, setHsv] = useState(() => rgbToHsv(hexToRgb(/^#[0-9a-f]{6}$/i.test(initialColor || '') ? initialColor : '#000000')));
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const rgb = hsvToRgb(hsv);
  const hex = rgbToHex(rgb);

  const dragTrack = (ref, onMove) => (e) => {
    e.preventDefault();
    const update = (clientX, clientY) => {
      const rect = ref.current.getBoundingClientRect();
      const x = Math.min(Math.max(0, clientX - rect.left), rect.width);
      const y = Math.min(Math.max(0, clientY - rect.top), rect.height);
      onMove(x / rect.width, y / rect.height);
    };
    update(e.clientX, e.clientY);
    const onMouseMove = (ev) => update(ev.clientX, ev.clientY);
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const setRgbField = (key, value) => {
    const n = clamp255(Number(value) || 0);
    setHsv(rgbToHsv({ ...rgb, [key]: n }));
  };

  return createPortal(
    <div className="psm-colors-dialog-overlay" onMouseDown={onClose}>
      <div className="psm-colors-dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="psm-colors-dialog-head">
          <strong>Colors</strong>
          <button type="button" className="psm-colors-dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="psm-colors-dialog-picker">
          <div
            ref={svRef}
            className="psm-colors-sv"
            style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))` }}
            onMouseDown={dragTrack(svRef, (x, y) => setHsv((c) => ({ ...c, s: x, v: 1 - y })))}
          >
            <div className="psm-colors-sv-cursor" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }} />
          </div>
          <div
            ref={hueRef}
            className="psm-colors-hue"
            onMouseDown={dragTrack(hueRef, (x, y) => setHsv((c) => ({ ...c, h: Math.min(359.99, y * 360) })))}
          >
            <div className="psm-colors-hue-cursor" style={{ top: `${(hsv.h / 360) * 100}%` }} />
          </div>
        </div>
        <div className="psm-colors-dialog-fields">
          <label className="psm-colors-dialog-field">
            <span>Red</span>
            <input type="number" min={0} max={255} value={rgb.r} onChange={(e) => setRgbField('r', e.target.value)} />
          </label>
          <label className="psm-colors-dialog-field">
            <span>Green</span>
            <input type="number" min={0} max={255} value={rgb.g} onChange={(e) => setRgbField('g', e.target.value)} />
          </label>
          <label className="psm-colors-dialog-field">
            <span>Blue</span>
            <input type="number" min={0} max={255} value={rgb.b} onChange={(e) => setRgbField('b', e.target.value)} />
          </label>
          <label className="psm-colors-dialog-field psm-colors-dialog-hex">
            <span>Hex</span>
            <input
              type="text"
              value={hex}
              onChange={(e) => {
                const v = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
                if (/^#[0-9a-f]{6}$/i.test(v)) setHsv(rgbToHsv(hexToRgb(v)));
              }}
            />
          </label>
        </div>
        <div className="psm-colors-dialog-preview">
          <div className="psm-colors-dialog-swatch-col">
            <span>New</span>
            <div className="psm-colors-dialog-swatch" style={{ background: hex }} />
          </div>
          <div className="psm-colors-dialog-swatch-col">
            <span>Current</span>
            <div className="psm-colors-dialog-swatch" style={{ background: /^#[0-9a-f]{6}$/i.test(initialColor || '') ? initialColor : '#ffffff' }} />
          </div>
        </div>
        <div className="psm-colors-dialog-actions">
          <button type="button" className="psm-block-add-item psm-group-config-create" onClick={() => onPick(hex)}>OK</button>
          <button type="button" className="psm-block-add-item psm-group-config-back" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Word-style color picker panel — Automatic, a Theme Colors grid (base color across the top,
// a tint/shade ladder running down each column), a flat Standard Colors row, and a "More
// Colors…" trigger opening MoreColorsDialog for any custom hex. Shared by both the text-color
// and highlight-color buttons (see RichTextField below) — onPick gets a hex value, or '' for
// Automatic (clears the override back to inherited/default).
const ColorPickerPanel = ({ onPick }) => {
  const [showMore, setShowMore] = useState(false);
  return (
    <div className="psm-color-picker-panel">
      <button type="button" className="psm-richtext-dropdown-item" onClick={() => onPick('')}>Automatic</button>
      <div className="psm-color-picker-label">Theme Colors</div>
      <div className="psm-color-picker-grid">
        {THEME_BASE_COLORS.map((base) => (
          <div key={base} className="psm-color-picker-col">
            {TINT_SHADE_STEPS.map((step) => {
              const c = step === 0 ? base : mixToward(base, step);
              return (
                <button key={step} type="button" className="psm-color-picker-swatch" style={{ background: c }} title={c} onClick={() => onPick(c)} />
              );
            })}
          </div>
        ))}
      </div>
      <div className="psm-color-picker-label">Standard Colors</div>
      <div className="psm-color-picker-grid psm-color-picker-grid-flat">
        {STANDARD_COLORS.map((c) => (
          <button key={c} type="button" className="psm-color-picker-swatch" style={{ background: c }} title={c} onClick={() => onPick(c)} />
        ))}
      </div>
      <button type="button" className="psm-richtext-dropdown-item psm-color-picker-more" onClick={() => setShowMore(true)}>
        <span className="psm-color-picker-more-icon" />
        More Colors…
      </button>
      {showMore && (
        <MoreColorsDialog
          initialColor=""
          onPick={(hex) => { onPick(hex); setShowMore(false); }}
          onClose={() => setShowMore(false)}
        />
      )}
    </div>
  );
};

// A paragraph field with real rich-text formatting (bold/italic/underline/bullets/color/font),
// stored as sanitized HTML. The toolbar sits pinned above the field while it's focused —
// simpler and more reliable than tracking the live text-selection position.
// singleLine: for short fields (button labels, pills, stat values) — blocks Enter from
// inserting a line break, since these render inline rather than as paragraphs.
// wrapperClassName: for fields whose className is actually a *positioning* class (e.g. the
// hero chips use position:absolute via .public-floating-chip) — that needs to land on the
// outer wrapper, not the inner editable div, or the absolute position resolves against the
// wrong containing block.
// align/onAlignChange/alignOptions, spacing/onSpacingChange, bgColor/onBgColorChange,
// linkValue/onLinkChange: opt-in block-level controls (used by freeform Text/Button/Chip
// blocks) that add extra sections to this same floating toolbar instead of a separate
// settings row (or, for Link, a separate floating field) sitting in the page — they only
// appear when the corresponding onChange prop is actually passed in, so every other use of
// this field (hero title, eyebrow, etc.) renders exactly as before. Link works the same way
// for any of the three block types — set a destination and that block becomes clickable —
// instead of being a button-only concept.
const RichTextField = ({
  className, wrapperClassName, value, onChange, placeholder, singleLine,
  align, onAlignChange, alignOptions, spacing, onSpacingChange, bgColor, onBgColorChange,
  linkValue, onLinkChange,
}) => {
  const editorRef = useRef(null);
  const toolbarRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const lastValueRef = useRef(null);
  // Reflects the actual formatting at the current caret/selection — Word's ribbon does the
  // same thing (Bold lit up, Font/Size showing the real values, the color swatches showing
  // what's actually there) instead of the toolbar always looking the same regardless of what
  // you clicked into.
  const [detected, setDetected] = useState({ bold: false, italic: false, underline: false, strike: false, ul: false, ol: false, color: '', highlight: '', font: '', size: '' });

  useEffect(() => {
    const node = editorRef.current;
    if (!node) return;
    // Only resync from external changes (undo/redo, page switch) while NOT focused —
    // overwriting innerHTML while the user is actively typing resets the caret to the start.
    if (!focused && value !== lastValueRef.current) {
      node.innerHTML = value || '';
      lastValueRef.current = value;
    }
  }, [value, focused]);


  // Walks up from the caret/selection to the field's own root, collecting the first
  // color/backgroundColor/fontFamily/fontSize it finds (closest wins, same as how CSS
  // inheritance would resolve it) — color/font/size are applied via inline <span style>
  // (see wrapSelectionWithStyle), not execCommand, so queryCommandValue can't see them; this
  // is the only way to detect what's "really there." Bold/italic/underline/strike/lists DO go
  // through execCommand, so queryCommandState already reflects those reliably.
  const detectFormat = useCallback(() => {
    const node = editorRef.current;
    if (!node) return;
    const selection = window.getSelection();
    let color = ''; let highlight = ''; let font = ''; let size = '';
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (node.contains(range.commonAncestorContainer)) {
        let el = range.startContainer;
        if (el.nodeType === 3) el = el.parentElement;
        while (el && node.contains(el)) {
          if (el.nodeType === 1) {
            if (!color && el.style.color) color = el.style.color;
            if (!highlight && el.style.backgroundColor) highlight = el.style.backgroundColor;
            if (!font && el.style.fontFamily) font = el.style.fontFamily.replace(/['"]/g, '');
            if (!size && el.style.fontSize) size = el.style.fontSize;
          }
          el = el.parentElement;
        }
      }
    }
    let bold = false; let italic = false; let underline = false; let strike = false; let ul = false; let ol = false;
    try {
      bold = document.queryCommandState('bold');
      italic = document.queryCommandState('italic');
      underline = document.queryCommandState('underline');
      strike = document.queryCommandState('strikeThrough');
      ul = document.queryCommandState('insertUnorderedList');
      ol = document.queryCommandState('insertOrderedList');
    } catch {}
    setDetected({ bold, italic, underline, strike, ul, ol, color, highlight, font, size });
  }, []);

  useEffect(() => {
    if (!focused) return;
    document.addEventListener('selectionchange', detectFormat);
    detectFormat();
    return () => document.removeEventListener('selectionchange', detectFormat);
  }, [focused, detectFormat]);

  const emitChange = () => {
    const html = editorRef.current?.innerHTML || '';
    lastValueRef.current = html;
    onChange(html);
  };

  // Always points at this render's emitChange (which closes over this render's onChange/
  // updateObjectListItem, i.e. the block as it exists *right now*, including any
  // align/spacing/background/link change just made via the toolbar). The effect below only
  // re-runs when `focused` changes, so without this ref it kept calling the emitChange from
  // whenever focus was first gained — a stale snapshot from before any of those toolbar
  // edits — and applying it on an outside click overwrote the block with that old snapshot,
  // discarding every change made via the toolbar in between.
  const emitChangeRef = useRef(emitChange);
  emitChangeRef.current = emitChange;

  // Belt-and-suspenders alongside onBlur below: native blur's relatedTarget is unreliable
  // for clicks that land on a plain non-focusable element (often comes through as null), so
  // a click on truly empty page space wasn't reliably closing the toolbar. This catches that
  // case directly regardless of what blur reports.
  useEffect(() => {
    if (!focused) return;
    const handleOutsideClick = (e) => {
      if (editorRef.current && editorRef.current.contains(e.target)) return;
      if (toolbarRef.current && toolbarRef.current.contains(e.target)) return;
      // MoreColorsDialog is its own separate portal (a centered modal, not anchored under
      // the toolbar), so a click inside it — dragging the picker, typing a hex value — isn't
      // a DOM descendant of toolbarRef and would otherwise look exactly like a click on the
      // page behind everything, closing the toolbar (and the dialog with it) mid-interaction.
      if (e.target.closest && e.target.closest('.psm-colors-dialog-overlay')) return;
      setFocused(false);
      setOpenDropdown(null);
      emitChangeRef.current();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [focused]);

  const runCommand = (command, arg) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
    detectFormat();
  };

  // Bold/italic/underline/bullets via execCommand produce tags already on our sanitizer
  // allowlist (b/i/u/ul/li). Color and font need a custom implementation instead of
  // execCommand('foreColor'/'fontName'): those produce legacy <font color>/<font face> tags,
  // which (a) the sanitizer strips entirely before the public site renders them, since only
  // <span style> is allowlisted, and (b) were unreliable about actually updating in place.
  const wrapSelectionWithStyle = (styleProps) => {
    const node = editorRef.current;
    if (!node) return;
    node.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!node.contains(range.commonAncestorContainer)) return;
    const span = document.createElement('span');
    Object.assign(span.style, styleProps);
    span.appendChild(range.extractContents());
    range.insertNode(span);
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
    emitChange();
    detectFormat();
  };

  const toggleDropdown = (name) => setOpenDropdown((current) => (current === name ? null : name));

  const isEmpty = !value || String(value).replace(/<[^>]+>/g, '').trim() === '';

  return (
    <div className={`psm-richtext-wrap ${wrapperClassName || ''}`} style={focused ? { paddingTop: '42px' } : undefined}>
      {focused && (
        <div ref={toolbarRef} className="psm-richtext-toolbar" onMouseDown={(e) => e.preventDefault()}>
          <button type="button" className={`psm-richtext-btn${detected.bold ? ' active' : ''}`} title="Bold" onClick={() => runCommand('bold')}><strong>B</strong></button>
          <button type="button" className={`psm-richtext-btn${detected.italic ? ' active' : ''}`} title="Italic" onClick={() => runCommand('italic')}><em>I</em></button>
          <button type="button" className={`psm-richtext-btn${detected.underline ? ' active' : ''}`} title="Underline" onClick={() => runCommand('underline')}><u>U</u></button>
          <button type="button" className={`psm-richtext-btn${detected.strike ? ' active' : ''}`} title="Strikethrough" onClick={() => wrapSelectionWithStyle({ textDecoration: 'line-through' })}><s>S</s></button>
          <span className="psm-richtext-sep" />
          <button type="button" className={`psm-richtext-btn${detected.ul ? ' active' : ''}`} title="Bullet list" onClick={() => runCommand('insertUnorderedList')}>≡</button>
          <button type="button" className={`psm-richtext-btn${detected.ol ? ' active' : ''}`} title="Numbered list" onClick={() => runCommand('insertOrderedList')}>1.</button>
          <button type="button" className="psm-richtext-btn" title="Decrease indent" onClick={() => runCommand('outdent')}>⇤</button>
          <button type="button" className="psm-richtext-btn" title="Increase indent" onClick={() => runCommand('indent')}>⇥</button>
          <span className="psm-richtext-sep" />
          <RichTextDropdown label={detected.font || 'Font'} isOpen={openDropdown === 'font'} onToggle={() => toggleDropdown('font')}>
            {RICH_TEXT_FONTS.map((font) => (
              <button
                key={font}
                type="button"
                className={`psm-richtext-dropdown-item${detected.font === font ? ' active' : ''}`}
                style={{ fontFamily: font }}
                onClick={() => { wrapSelectionWithStyle({ fontFamily: font }); setOpenDropdown(null); }}
              >
                {font}
              </button>
            ))}
          </RichTextDropdown>
          <RichTextDropdown label={detected.size ? `${parseFloat(detected.size)}` : 'Size'} isOpen={openDropdown === 'size'} onToggle={() => toggleDropdown('size')}>
            {RICH_TEXT_SIZES.map((pt) => (
              <button
                key={pt}
                type="button"
                className={`psm-richtext-dropdown-item${parseFloat(detected.size) === pt ? ' active' : ''}`}
                onClick={() => { wrapSelectionWithStyle({ fontSize: `${pt}pt` }); setOpenDropdown(null); }}
              >
                {pt}
              </button>
            ))}
          </RichTextDropdown>
          <span className="psm-richtext-sep" />
          <RichTextDropdown
            label={<span className="psm-richtext-color-trigger">A<span className="psm-richtext-color-trigger-bar" style={{ background: detected.color || '#000000' }} /></span>}
            isOpen={openDropdown === 'textColor'}
            onToggle={() => toggleDropdown('textColor')}
          >
            <ColorPickerPanel onPick={(c) => { wrapSelectionWithStyle({ color: c }); setOpenDropdown(null); }} />
          </RichTextDropdown>
          <RichTextDropdown
            label={<span className="psm-richtext-color-trigger psm-richtext-highlight-trigger">H<span className="psm-richtext-color-trigger-bar" style={{ background: detected.highlight || 'transparent' }} /></span>}
            isOpen={openDropdown === 'highlight'}
            onToggle={() => toggleDropdown('highlight')}
          >
            <ColorPickerPanel onPick={(c) => { wrapSelectionWithStyle({ backgroundColor: c }); setOpenDropdown(null); }} />
          </RichTextDropdown>
          <span className="psm-richtext-sep" />
          <button type="button" className="psm-richtext-btn" title="Clear formatting" onClick={() => runCommand('removeFormat')}>Clear</button>
          {onAlignChange && (
            <>
              <span className="psm-richtext-sep" />
              {(alignOptions || ['left', 'center', 'right', 'justify']).map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`psm-richtext-btn${(align || 'left') === a ? ' active' : ''}`}
                  title={BLOCK_ALIGN_TITLES[a]}
                  onClick={() => onAlignChange(a)}
                >
                  <AlignIcon align={a} />
                </button>
              ))}
            </>
          )}
          {onSpacingChange && (
            <>
              <span className="psm-richtext-sep" />
              <RichTextDropdown label="Spacing" isOpen={openDropdown === 'spacing'} onToggle={() => toggleDropdown('spacing')}>
                {LINE_SPACING_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`psm-richtext-dropdown-item${(spacing || 1.7) === n ? ' active' : ''}`}
                    onClick={() => { onSpacingChange(n); setOpenDropdown(null); }}
                  >
                    {n.toFixed(2).replace(/0$/, '')}
                  </button>
                ))}
              </RichTextDropdown>
            </>
          )}
          {onBgColorChange && (
            <>
              <span className="psm-richtext-sep" />
              <RichTextDropdown label="Background" isOpen={openDropdown === 'background'} onToggle={() => toggleDropdown('background')}>
                <div className="psm-richtext-bg-panel">
                  <input
                    type="color"
                    className="psm-richtext-bg-swatch"
                    title="Background color"
                    value={/^#[0-9a-f]{6}$/i.test(bgColor || '') ? bgColor : '#ffffff'}
                    onChange={(e) => onBgColorChange(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  <button type="button" className="psm-richtext-dropdown-item" onClick={() => { onBgColorChange(''); setOpenDropdown(null); }}>No background</button>
                </div>
              </RichTextDropdown>
            </>
          )}
          {onLinkChange && (
            <>
              <span className="psm-richtext-sep" />
              <RichTextDropdown label="Link" isOpen={openDropdown === 'link'} onToggle={() => toggleDropdown('link')}>
                <div className="psm-richtext-link-panel">
                  <input
                    type="text"
                    className="psm-richtext-link-input"
                    placeholder="/path or https://…"
                    value={linkValue || ''}
                    onChange={(e) => onLinkChange(e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  {linkValue && (
                    <button type="button" className="psm-richtext-dropdown-item" onClick={() => { onLinkChange(''); setOpenDropdown(null); }}>Remove link</button>
                  )}
                </div>
              </RichTextDropdown>
            </>
          )}
        </div>
      )}
      <div
        ref={editorRef}
        className={`psm-editable psm-richtext-editor${isEmpty ? ' psm-richtext-empty' : ''}${singleLine ? ' psm-richtext-single' : ''} ${className || ''}`}
        style={onSpacingChange ? { lineHeight: spacing || undefined } : undefined}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          // Clicking a native <input type="color"> or <input type="text"> inside a toolbar
          // dropdown panel (Background, Link) takes focus — ignore that blur so the toolbar
          // stays visible while the user interacts with those inputs.
          if (toolbarRef.current && e.relatedTarget && toolbarRef.current.contains(e.relatedTarget)) return;
          setFocused(false);
          setOpenDropdown(null);
          emitChange();
        }}
        onInput={emitChange}
        onKeyDown={singleLine ? (e) => { if (e.key === 'Enter') e.preventDefault(); } : undefined}
        onPaste={(e) => {
          // Pasting from a live page (or Word/Google Docs) carries over the source's own
          // HTML — its own line-height, margins, font sizes, <div>-per-line structure —
          // which rendered fine here (nothing strips it while editing) but then looked
          // completely different on the public site, since the sanitizer there strips any
          // tag/style outside our own allowlist. Forcing plain text on paste means what you
          // see in the editor always matches what survives sanitization, and pasted content
          // always inherits this field's own styling instead of fighting it.
          e.preventDefault();
          // Browsers' plain-text clipboard extraction often substitutes real non-breaking
          // space characters (U+00A0) for regular spaces (to preserve visually-collapsed
          // whitespace from the source page) — those don't wrap, so a heading copied from a
          // live page could render fine here (still narrow enough to fit) but overflow once
          // placed in a narrower column on the actual public page. Normalize to regular
          // breakable spaces so pasted text always wraps the same way everywhere.
          const text = e.clipboardData.getData('text/plain').replace(/\u00A0/g, ' ');
          document.execCommand('insertText', false, text);
        }}
      />
    </div>
  );
};

// A simple picture-frame icon for the empty-image placeholder — plain text alone read as
// unfinished, this gives it the familiar "image goes here" look without pulling in an icon
// library for one glyph.
const ImagePlaceholderIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M21 15l-5-5-4 4-3-3-6 6" />
  </svg>
);

// Freeform blocks render the image at its natural aspect ratio on the public site (no fixed
// height/object-fit:cover), so the editor preview uses the exact same public-* image class
// directly, instead of a fixed-box crop that would show different framing than live.
const BlockImageField = ({ className, src, alt, onUpload, folder, path }) => {
  const inputRef = useRef(null);
  return (
    <div className="psm-block-image-field" onClick={() => inputRef.current?.click()}>
      {src
        ? <img src={src} alt={alt || ''} className={className} />
        : <div className="psm-editable-image-empty"><ImagePlaceholderIcon /><span>Click to upload image</span></div>}
      <div className="psm-editable-image-overlay">
        <span>{src ? 'Click to replace' : 'Click to upload'}</span>
      </div>
      {/* No remove-just-the-image button here — this is always used within a removable
          block (Image, Solution) that already has its own "Remove block" control nearby. */}
      <input ref={inputRef} type="file" accept="image/*,.svg" hidden onChange={(e) => onUpload(e, path, folder)} />
    </div>
  );
};

// Every insertable block type for the freeform "extra blocks" area, with a factory for its
// default shape. "container" is excluded from the menu when already inside a container (see
// allowContainer below) — nesting is capped at one level deep to keep this manageable.
// "container" (a nested box-within-the-Proof-column) was deprecated in favor of full
// page-width "Custom Sections" (see addCustomSection/+ Add Section) and is intentionally
// not offered here anymore — existing saved container blocks still render fine, they're
// just no longer something you can insert going forward.
// Image/Button/Chip/Stat/Solution used to be standalone options here too — removed since
// every one of them now goes through GROUP_TYPE_OPTIONS below instead (a "group" of just 1
// item is exactly a single item, with the same count/columns popup creating it). Still
// rendered normally by renderBlockList if a page saved before this change already has one.
const BLOCK_TYPE_OPTIONS = [
  { type: 'text', label: 'Text', factory: () => ({ type: 'text', html: '' }) },
  { type: 'divider', label: 'Line', factory: () => ({ type: 'divider' }) },
  { type: 'gap', label: 'Gap', factory: () => ({ type: 'gap', size: 'medium' }) },
];

// A Group holds N items of one repeating kind (buttons, chips, stats, images, or solutions —
// never Text, which doesn't make sense as a repeating grid) arranged into a chosen number of
// equal-width columns, created up front via BlockAddMenu's count/columns popup instead of
// adding items one at a time and hoping they happen to fit a row.
const GROUP_TYPE_OPTIONS = [
  { groupType: 'button', label: 'Button', factory: () => ({ type: 'button', label: 'New button', to: '/' }) },
  { groupType: 'chip', label: 'Chip', factory: () => ({ type: 'chip', html: 'New chip' }) },
  { groupType: 'stat', label: 'Stat', factory: () => ({ type: 'stat', value: '0', label: 'Label' }) },
  { groupType: 'image', label: 'Image', factory: () => ({ type: 'image', src: '', alt: '' }) },
  { groupType: 'solution', label: 'Solution', factory: () => ({ type: 'solution', image: '', alt: '', title: 'New solution', text: '' }) },
];

// A Group's max columns scales with its parent container's width — each column represents
// the same absolute slot size regardless of container width (Full is 12 units, so Quarter
// gets 3, Half gets 6, Three-quarter gets 9), instead of letting a narrow container allow the
// same column count as a full-width one and squeeze every item.
const GROUP_MAX_COLUMNS = { quarter: 3, half: 6, 'three-quarter': 9, full: 12 };

const CONTAINER_WIDTH_LABELS = { quarter: ['Quarter', 'S'], half: ['Half', 'M'], 'three-quarter': ['Three-quarter', 'L'], full: ['Full width', 'XL'] };

// A single "Size" trigger that opens a dropdown of S/M/L/XL, instead of 4 separate circular
// buttons competing for space in an already-crowded topbar (drag handle, label, "+ Add Item",
// this, and Remove all share the same 24px-tall strip). Same visual pattern as "+ Add Item".
const SizeDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const current = value || 'full';
  return (
    <div className="psm-size-dropdown" ref={ref}>
      <button type="button" className="psm-add-ghost-pill psm-block-add-trigger" onClick={() => setOpen((o) => !o)}>Size ▾</button>
      {open && (
        <div className="psm-size-dropdown-panel">
          {Object.keys(CONTAINER_WIDTH_LABELS).map((w) => {
            const [title, short] = CONTAINER_WIDTH_LABELS[w];
            return (
              <button
                key={w}
                type="button"
                className={`psm-block-add-item${current === w ? ' active' : ''}`}
                onClick={() => { onChange(w); setOpen(false); }}
              >
                {title} ({short})
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Edits an EXISTING group's "Per row" column count — the create-time config panel (see
// BlockAddMenu) also asks "How many?", but that field is deliberately left out here: a
// group's item count is already safely adjustable one at a time via the existing "+" (adds
// one) and each item's own "×" (removes one), so a bulk count field on an existing group
// would just be a riskier way to do the same thing — decreasing it would silently delete
// whichever items end up past the new count, with no preview of which ones. "Per row" has no
// such risk (it never touches the items themselves), so it's the only thing editable here.
const GroupColumnsDropdown = ({ value, maxColumns, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  return (
    <div className="psm-size-dropdown" ref={ref}>
      <button type="button" className="psm-group-edit-btn" title="Edit items per row" onClick={() => setOpen((o) => !o)}>✎</button>
      {open && (
        <div className="psm-size-dropdown-panel psm-group-columns-panel">
          <span className="psm-group-columns-panel-label">Per row</span>
          <div className="psm-group-config-columns">
            {Array.from({ length: maxColumns }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                className={`psm-block-width-btn${(value || 4) === n ? ' active' : ''}`}
                onClick={() => { onChange(n); setOpen(false); }}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const BORDER_STYLE_OPTIONS = [
  { value: 'none', label: 'No border' },
  { value: 'solid', label: 'Line border' },
  { value: 'solid-thick', label: 'Thicker line border' },
  { value: 'dotted', label: 'Dotted border' },
  { value: 'dashed', label: 'Dashed border' },
  { value: 'bottom', label: 'Only line at the bottom' },
  { value: 'bottom-thick', label: 'Only thick line at the bottom' },
];

// Container background — same "swatch + reset" idea as the rich-text toolbar's own
// Background dropdown, just a standalone trigger here instead of one section of a bigger
// floating toolbar.
const ContainerBackgroundDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  return (
    <div className="psm-size-dropdown" ref={ref}>
      <button type="button" className="psm-style-trigger" onClick={() => setOpen((o) => !o)}>Background ▾</button>
      {open && (
        <div className="psm-size-dropdown-panel psm-container-bg-panel">
          <input
            type="color"
            className="psm-container-color-swatch"
            title="Background colour"
            value={/^#[0-9a-f]{6}$/i.test(value || '') ? value : '#ffffff'}
            onChange={(e) => onChange(e.target.value)}
          />
          <button type="button" className="psm-block-add-item" onClick={() => { onChange(''); setOpen(false); }}>No background</button>
        </div>
      )}
    </div>
  );
};

// Border style + colour together in one dropdown, since a colour choice is meaningless
// without a style to apply it to — picking a style doesn't close the panel (unlike Size),
// so colour can still be adjusted right after without reopening anything.
const ContainerBorderDropdown = ({ borderStyle, borderColor, onStyleChange, onColorChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const current = borderStyle || 'dashed';
  return (
    <div className="psm-size-dropdown" ref={ref}>
      <button type="button" className="psm-style-trigger" onClick={() => setOpen((o) => !o)}>Border ▾</button>
      {open && (
        <div className="psm-size-dropdown-panel psm-container-border-panel">
          {BORDER_STYLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`psm-block-add-item${current === opt.value ? ' active' : ''}`}
              onClick={() => onStyleChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
          <div className="psm-container-border-color-row">
            <span>Border colour</span>
            <input
              type="color"
              className="psm-container-color-swatch"
              title="Border colour"
              value={/^#[0-9a-f]{6}$/i.test(borderColor || '') ? borderColor : '#9ca3af'}
              onChange={(e) => onColorChange(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Computes the actual border/background inline style for a container from its
// background/borderStyle/borderColor fields — shared by the editor and the live page so a
// container/group/section always renders identically in both. fallbackBorderStyle matters
// because each of those three had a *different* pre-existing default before this feature
// existed: Containers always had a 1px dashed border baked into their CSS class, but Groups
// and Sections never had a border at all — defaulting everything to 'dashed' here would have
// silently added a border to every Group/Section that never had one.
const getContainerStyle = (block, fallbackBorderStyle = 'dashed') => {
  const color = /^#[0-9a-f]{6}$/i.test(block.borderColor || '') ? block.borderColor : '#9ca3af';
  const styleKey = block.borderStyle || fallbackBorderStyle;
  const style = { background: block.background || undefined };
  if (styleKey === 'none') {
    style.border = 'none';
  } else if (styleKey === 'bottom') {
    style.border = 'none';
    style.borderBottom = `1px solid ${color}`;
  } else if (styleKey === 'bottom-thick') {
    style.border = 'none';
    style.borderBottom = `3px solid ${color}`;
  } else if (styleKey === 'solid-thick') {
    style.border = `3px solid ${color}`;
  } else {
    style.border = `1px ${styleKey} ${color}`;
  }
  return style;
};

// Same border-style logic as getContainerStyle, but for Groups specifically — see
// PublicSite.js's identical helper for why (outline doesn't participate in box-model sizing
// the way border does, which is required now that a Group's card is width:fit-content).
const getGroupContainerStyle = (block) => {
  const color = /^#[0-9a-f]{6}$/i.test(block.borderColor || '') ? block.borderColor : '#9ca3af';
  const styleKey = block.borderStyle || 'none';
  const style = { background: block.background || undefined, border: 'none' };
  if (styleKey === 'bottom') {
    style.borderBottom = `1px solid ${color}`;
  } else if (styleKey === 'bottom-thick') {
    style.borderBottom = `3px solid ${color}`;
  } else if (styleKey === 'solid-thick') {
    style.outline = `3px solid ${color}`;
  } else if (styleKey !== 'none') {
    style.outline = `1px ${styleKey} ${color}`;
  }
  return style;
};

// Single "+ Add block" trigger that opens a dropdown of every block type, instead of a row
// of 9 separate buttons competing for space (especially cramped inside a nested container).
// maxColumns caps the Group config form's column count to whatever its parent container's
// width actually allows (see GROUP_MAX_COLUMNS) — defaults to 12 (Full) for menus that don't
// pass one, i.e. the top-level "+ Add Item" that auto-wraps into a new Full-width container.
const BlockAddMenu = ({ onAdd, maxColumns = 12 }) => {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  // Selecting a Group type doesn't add anything immediately — it switches the panel to a
  // small count/columns form instead, so a "Create" step exists before N items get added.
  const [configuring, setConfiguring] = useState(null);
  const [count, setCount] = useState(4);
  const [columns, setColumns] = useState(4);
  const menuRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // The panel is portaled to document.body, so it's not a DOM descendant of menuRef —
    // without also checking panelRef here, every click inside the panel itself counted as
    // "outside", closing the menu on mousedown before the option's own onClick could fire.
    const handleOutsideClick = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Closing (for any reason — outside click, Create, picking a non-group option) always
  // drops back to the plain option list next time it opens, instead of reopening mid-way
  // through configuring whatever was selected last time.
  useEffect(() => {
    if (open) return;
    setConfiguring(null);
    setCount(4);
    setColumns(4);
  }, [open]);

  // Portaled to document.body and positioned fixed to the viewport — this menu lives inside
  // dashed section/container boxes nested inside .psm-wysiwyg-canvas, which has
  // overflow:hidden (for its rounded corners), so a plain position:absolute panel gets
  // silently clipped before it can fully open. Same fix as the rich-text toolbar.
  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const node = menuRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      // First pass, before the panel has ever mounted (so its real height isn't known yet) —
      // guess "below the trigger." The follow-up effect below corrects this to "above the
      // trigger" once the panel is actually in the DOM and overflows the viewport bottom.
      setPanelPos({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  // Long option lists (Text/Image/Button/Chip/Divider/Gap/Stat/Solution/Container) opened
  // near the bottom of the viewport ran off-screen with no way to see or click the rest —
  // once the panel's actual height is known, flip it to open upward instead if it would
  // overflow, the same way a native <select> or context menu would.
  useEffect(() => {
    if (!open || !panelPos || !panelRef.current || !menuRef.current) return;
    const panelHeight = panelRef.current.getBoundingClientRect().height;
    const rect = menuRef.current.getBoundingClientRect();
    const overflowsBottom = rect.bottom + 4 + panelHeight > window.innerHeight - 8;
    if (!overflowsBottom) return;
    const flippedTop = Math.max(8, rect.top - panelHeight - 4);
    if (Math.abs(flippedTop - panelPos.top) > 0.5) {
      setPanelPos((p) => ({ ...p, top: flippedTop }));
    }
    // configuring changes the panel's content (option list vs the count/columns form), and
    // therefore its height — re-checking the flip is what makes switching into the form not
    // leave it overflowing the viewport when the list above it fit but the form doesn't.
  }, [open, panelPos, configuring]);

  return (
    <div className="psm-block-add-menu" ref={menuRef}>
      <button type="button" className="psm-add-ghost-pill psm-block-add-trigger" onClick={() => setOpen((o) => !o)}>+ Add Item ▾</button>
      {open && panelPos && createPortal(
        <div ref={panelRef} className="psm-block-add-panel" style={{ top: panelPos.top, left: panelPos.left }}>
          {configuring ? (
            <div className="psm-group-config">
              <div className="psm-group-config-title">{configuring.label}</div>
              <label className="psm-group-config-field">
                <span>How many?</span>
                <input
                  type="number"
                  min={1}
                  max={48}
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(48, Number(e.target.value) || 1)))}
                />
              </label>
              <label className="psm-group-config-field">
                <span>Per row</span>
                <div className="psm-group-config-columns">
                  {Array.from({ length: maxColumns }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`psm-block-width-btn${columns === n ? ' active' : ''}`}
                      onClick={() => setColumns(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </label>
              <div className="psm-group-config-actions">
                <button type="button" className="psm-block-add-item psm-group-config-back" onClick={() => setConfiguring(null)}>← Back</button>
                <button
                  type="button"
                  className="psm-block-add-item psm-group-config-create"
                  onClick={() => {
                    onAdd({
                      type: 'group',
                      groupType: configuring.groupType,
                      columns,
                      children: Array.from({ length: count }, () => configuring.factory()),
                    });
                    setOpen(false);
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <>
              {BLOCK_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  className="psm-block-add-item"
                  onClick={() => { onAdd(opt.factory()); setOpen(false); }}
                >
                  {opt.label}
                </button>
              ))}
              <div className="psm-block-add-divider" />
              {GROUP_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.groupType}
                  type="button"
                  className="psm-block-add-item"
                  onClick={() => { setConfiguring(opt); setColumns(Math.min(4, maxColumns)); }}
                >
                  {opt.label}
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};



// The universal page editor — every public page (Home included) is a blank canvas built
// entirely from Custom Sections/Containers/Groups. Originally a separate, deliberately
// standalone "Test Page" sandbox so experimenting here couldn't risk breaking Home's old
// dedicated hero editor; now that every page works this same way, it's just the editor.
const PageEditor = ({ value, onChange, onUpload, branding, backgroundColor, navEditor, storeName }) => {
  const { wrapperRef: canvasScaleWrapRef, canvasRef } = useScaledCanvas();
  const updateField = (path, nextValue) => onChange((current) => setByPath(current, path, nextValue));
  const getList = (path) => (Array.isArray(getByPath(value, path)) ? getByPath(value, path) : []);
  const removeListItem = (path, index) => updateField(path, getList(path).filter((_, i) => i !== index));
  const updateObjectListItem = (path, index, patch) => {
    const list = [...getList(path)];
    list[index] = { ...list[index], ...patch };
    updateField(path, list);
  };
  const addObjectListItem = (path, item) => updateField(path, [...getList(path), item]);

  const [dragInfo, setDragInfo] = useState({ key: null, from: null, over: null });
  const makeDragHandlers = (key, path) => ({
    isSource: (index) => dragInfo.key === key && dragInfo.from === index,
    isOver: (index) => dragInfo.key === key && dragInfo.over === index && dragInfo.from !== index,
    onDragStart: (index) => (e) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
      setDragInfo({ key, from: index, over: null });
    },
    onDragOver: (index) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dragInfo.key === key && dragInfo.over !== index) setDragInfo((s) => ({ ...s, over: index }));
    },
    onDrop: (index) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (dragInfo.key !== key || dragInfo.from === null || dragInfo.from === index) {
        setDragInfo({ key: null, from: null, over: null });
        return;
      }
      const list = [...getList(path)];
      const [moved] = list.splice(dragInfo.from, 1);
      list.splice(index, 0, moved);
      updateField(path, list);
      setDragInfo({ key: null, from: null, over: null });
    },
    onDragEnd: () => setDragInfo({ key: null, from: null, over: null }),
  });

  const renderGroupItem = (groupPath, groupType, item, i, groupDrag, onRemoveItem) => {
    const itemPath = `${groupPath}.${i}`;
    let content;
    if (groupType === 'button') {
      content = (
        <span className="psm-editable-button-pill public-hero-secondary" style={item.bgColor ? { background: item.bgColor } : undefined}>
          <RichTextField
            className="psm-editable-button-label"
            singleLine
            value={item.label}
            placeholder="Label"
            onChange={(v) => updateObjectListItem(groupPath, i, { label: v })}
            bgColor={item.bgColor}
            onBgColorChange={(c) => updateObjectListItem(groupPath, i, { bgColor: c })}
            linkValue={item.to}
            onLinkChange={(v) => updateObjectListItem(groupPath, i, { to: v })}
          />
        </span>
      );
    } else if (groupType === 'chip') {
      content = (
        <span className="public-feature-pill psm-editable-pill" style={item.bgColor ? { background: item.bgColor } : undefined}>
          <RichTextField
            className="psm-editable-pill-input"
            singleLine
            value={item.html}
            placeholder="Chip text"
            onChange={(v) => updateObjectListItem(groupPath, i, { html: v })}
            bgColor={item.bgColor}
            onBgColorChange={(c) => updateObjectListItem(groupPath, i, { bgColor: c })}
            linkValue={item.to}
            onLinkChange={(v) => updateObjectListItem(groupPath, i, { to: v })}
          />
        </span>
      );
    } else if (groupType === 'image') {
      content = (
        <BlockImageField
          className="public-proof-block-image"
          src={item.src}
          alt={item.alt}
          onUpload={onUpload}
          folder="home-proof"
          path={`${itemPath}.src`}
        />
      );
    } else if (groupType === 'solution') {
      content = (
        <div className="public-solution-card psm-block-solution-preview">
          <BlockImageField
            className="public-solution-image"
            src={item.image}
            alt={item.alt}
            onUpload={onUpload}
            folder="home-proof-solutions"
            path={`${itemPath}.image`}
          />
          <div className="public-solution-copy">
            <RichTextField className="psm-h3-edit" value={item.title} placeholder="Title" onChange={(v) => updateObjectListItem(groupPath, i, { title: v })} />
            <RichTextField className="psm-p-edit" value={item.text} placeholder="Description" onChange={(v) => updateObjectListItem(groupPath, i, { text: v })} />
          </div>
        </div>
      );
    } else {
      content = (
        <div className="public-metric-card psm-block-stat-preview">
          <RichTextField className="psm-stat-value" singleLine value={item.value} placeholder="0" onChange={(v) => updateObjectListItem(groupPath, i, { value: v })} />
          <RichTextField className="psm-stat-label" singleLine value={item.label} placeholder="Label" onChange={(v) => updateObjectListItem(groupPath, i, { label: v })} />
        </div>
      );
    }
    return (
      <div
        key={i}
        className={`public-group-item psm-group-item${groupDrag.isOver(i) ? ' psm-drag-over' : ''}${groupDrag.isSource(i) ? ' psm-drag-source' : ''}`}
        onDragOver={groupDrag.onDragOver(i)}
        onDrop={groupDrag.onDrop(i)}
      >
        <span className="psm-item-drag-handle" title="Drag to reorder" draggable onDragStart={groupDrag.onDragStart(i)} onDragEnd={groupDrag.onDragEnd}>⠿</span>
        {content}
        <button type="button" className="psm-editable-remove-x" title="Remove" onClick={() => onRemoveItem(i)}>×</button>
      </div>
    );
  };

  const renderBlockList = (path, depth, { hideTrailingAdd, maxColumns = 12 } = {}) => {
    const dragHandlers = makeDragHandlers(path, path);
    // Reuses .public-proof-blocks directly (same class the live page renders with, not a
    // separate psm-* copy) — gap, width fractions, and nested-vs-top-level margin all come
    // from one source of truth now, instead of two numbers that have to be kept in sync by
    // hand and kept drifting apart. psm-items-row only adds an editor-specific top margin.
    return (
      <div className={`public-proof-blocks${depth === 0 ? ' psm-items-row' : ''}`}>
        {getList(path).map((block, index) => {
          const itemPath = `${path}.${index}`;
          // Button/chip render as compact pills with hover-revealed drag handle, remove ×,
          // and link field — matching the public site's bare pill exactly, instead of the
          // generic .psm-block-item card whose permanent border/padding/drag-handle made
          // every button render noticeably wider here than its live counterpart (which is
          // why fewer fit per row in the editor than actually fit on the public page).
          if (['button', 'chip'].includes(block.type)) {
            return (
              <div
                key={index}
                className={`psm-block-pill-wrap${dragHandlers.isOver(index) ? ' psm-drag-over' : ''}${dragHandlers.isSource(index) ? ' psm-drag-source' : ''}`}
                onDragOver={dragHandlers.onDragOver(index)}
                onDrop={dragHandlers.onDrop(index)}
              >
                <span className="psm-item-drag-handle psm-block-pill-drag" title="Drag to reorder" draggable onDragStart={dragHandlers.onDragStart(index)} onDragEnd={dragHandlers.onDragEnd}>⠿</span>
                {block.type === 'button' ? (
                  <span className="psm-editable-button-pill public-hero-secondary" style={block.bgColor ? { background: block.bgColor } : undefined}>
                    <RichTextField
                      className="psm-editable-button-label"
                      singleLine
                      value={block.label}
                      placeholder="Label"
                      onChange={(v) => updateObjectListItem(path, index, { label: v })}
                      bgColor={block.bgColor}
                      onBgColorChange={(c) => updateObjectListItem(path, index, { bgColor: c })}
                      linkValue={block.to}
                      onLinkChange={(v) => updateObjectListItem(path, index, { to: v })}
                    />
                    <button type="button" className="psm-editable-remove-x" title="Remove button" onClick={() => removeListItem(path, index)}>×</button>
                  </span>
                ) : (
                  <span className="public-feature-pill psm-editable-pill" style={block.bgColor ? { background: block.bgColor } : undefined}>
                    <RichTextField
                      className="psm-editable-pill-input"
                      singleLine
                      value={block.html}
                      placeholder="Chip text"
                      onChange={(v) => updateObjectListItem(path, index, { html: v })}
                      bgColor={block.bgColor}
                      onBgColorChange={(c) => updateObjectListItem(path, index, { bgColor: c })}
                      linkValue={block.to}
                      onLinkChange={(v) => updateObjectListItem(path, index, { to: v })}
                    />
                    <button type="button" className="psm-editable-remove-x" title="Remove chip" onClick={() => removeListItem(path, index)}>×</button>
                  </span>
                )}
              </div>
            );
          }
          // Every other item except "container" always fills 100% of whatever it's inside —
          // the container already owns width control (S/M/L/XL), so a separate per-item width
          // choice was redundant and, worse, compounded with a narrow container to
          // over-narrow the item itself (that's what was forcing "Text" to wrap mid-word and
          // grow taller than it needed to).
          const widthKey = block.type === 'container' ? (block.width || 'full') : 'large';
          // public-proof-block-width-* (not a separate psm-* copy) — same reasoning as the
          // items row above: one set of width-fraction numbers, shared by editor and live.
          const sizingClass = ['divider', 'gap'].includes(block.type)
            ? 'psm-block-item-full'
            : `public-proof-block-width-${widthKey}`;
          const isContainer = block.type === 'container';
          const isGroup = block.type === 'group';
          const groupOption = isGroup ? GROUP_TYPE_OPTIONS.find((g) => g.groupType === block.groupType) : null;
          const groupDrag = isGroup ? makeDragHandlers(`${itemPath}.children`, `${itemPath}.children`) : null;
          return (
            <div
              key={index}
              className={`psm-block-item ${sizingClass}${isContainer ? ' public-proof-block-container' : ''}${isGroup ? ' psm-block-item-group' : ''}${dragHandlers.isOver(index) ? ' psm-drag-over' : ''}${dragHandlers.isSource(index) ? ' psm-drag-source' : ''}`}
              style={isContainer ? getContainerStyle(block) : undefined}
              onDragOver={dragHandlers.onDragOver(index)}
              onDrop={dragHandlers.onDrop(index)}
            >
              {/* Containers and Groups get their own hover-only drag handle inside their
                  topbar instead (see below) — this one would otherwise take permanent flex
                  space pushing their content away from the wrapper's left edge. */}
              {!isContainer && !isGroup && (
                <span className="psm-item-drag-handle" title="Drag to reorder" draggable onDragStart={dragHandlers.onDragStart(index)} onDragEnd={dragHandlers.onDragEnd}>⠿</span>
              )}
              <div className="psm-block-item-body">
                {block.type === 'image' ? (
                  <BlockImageField
                    className="public-proof-block-image"
                    src={block.src}
                    alt={block.alt}
                    onUpload={onUpload}
                    onDelete={() => updateObjectListItem(path, index, { src: '' })}
                    folder="test-page"
                    path={`${itemPath}.src`}
                  />
                ) : block.type === 'divider' ? (
                  <hr className="psm-block-divider-preview" />
                ) : block.type === 'gap' ? (
                  <div className="psm-block-gap-preview">
                    <span>Gap — {block.size === 'small' ? 'Small' : block.size === 'large' ? 'Large' : 'Medium'}</span>
                  </div>
                ) : block.type === 'stat' ? (
                  <div className="public-metric-card psm-block-stat-preview">
                    <RichTextField className="psm-stat-value" singleLine value={block.value} placeholder="0" onChange={(v) => updateObjectListItem(path, index, { value: v })} />
                    <RichTextField className="psm-stat-label" singleLine value={block.label} placeholder="Label" onChange={(v) => updateObjectListItem(path, index, { label: v })} />
                  </div>
                ) : block.type === 'solution' ? (
                  <div className="public-solution-card psm-block-solution-preview">
                    <BlockImageField
                      className="public-solution-image"
                      src={block.image}
                      alt={block.alt}
                      onUpload={onUpload}
                      onDelete={() => updateObjectListItem(path, index, { image: '' })}
                      folder="test-page"
                      path={`${itemPath}.image`}
                    />
                    <div className="public-solution-copy">
                      <RichTextField className="psm-h3-edit" value={block.title} placeholder="Title" onChange={(v) => updateObjectListItem(path, index, { title: v })} />
                      <RichTextField className="psm-p-edit" value={block.text} placeholder="Description" onChange={(v) => updateObjectListItem(path, index, { text: v })} />
                    </div>
                  </div>
                ) : block.type === 'group' ? (
                  <div className="psm-group-preview">
                    <div className="psm-block-container-topbar psm-group-topbar">
                      <span className="psm-item-drag-handle psm-block-container-drag" title="Drag to reorder" draggable onDragStart={dragHandlers.onDragStart(index)} onDragEnd={dragHandlers.onDragEnd}>⠿</span>
                      <span className="psm-block-container-label">{groupOption?.label || 'Group'}</span>
                      <button
                        type="button"
                        className="psm-group-add-btn"
                        title={`Add ${block.groupType}`}
                        onClick={() => addObjectListItem(`${itemPath}.children`, groupOption ? groupOption.factory() : {})}
                      >
                        +
                      </button>
                      <GroupColumnsDropdown value={block.columns} maxColumns={maxColumns} onChange={(n) => updateObjectListItem(path, index, { columns: n })} />
                      <div className="psm-container-controls-right">
                        <ContainerBackgroundDropdown value={block.background} onChange={(v) => updateObjectListItem(path, index, { background: v })} />
                        <ContainerBorderDropdown
                          borderStyle={block.borderStyle}
                          borderColor={block.borderColor}
                          onStyleChange={(v) => updateObjectListItem(path, index, { borderStyle: v })}
                          onColorChange={(v) => updateObjectListItem(path, index, { borderColor: v })}
                        />
                      </div>
                      <button type="button" className="psm-block-container-remove psm-group-remove" title="Remove group" onClick={() => removeListItem(path, index)}>×</button>
                    </div>
                    {/* The topbar above stays anchored to the full-width .psm-group-preview
                        (plenty of room for its buttons regardless of how narrow the group
                        ends up) — this anchor+card split (matching the live page's own
                        .public-proof-block-group-anchor/.public-proof-block-group) is what
                        lets the card itself shrink-wrap to the actual items instead of
                        showing empty background past the last one in an under-filled row. */}
                    <div className="public-proof-block-group-anchor">
                      <div className="public-proof-block-group" style={getGroupContainerStyle(block)}>
                        <div className="public-group-row psm-group-row" style={{ '--psm-group-columns': block.columns || 4 }}>
                          {(block.children || []).map((item, i) => renderGroupItem(`${itemPath}.children`, block.groupType, item, i, groupDrag, (removeIndex) => {
                            // Removing the last item would leave an empty, useless group
                            // sitting on the page with nothing inside it — remove the group
                            // itself instead of just emptying it.
                            if ((block.children || []).length <= 1) {
                              removeListItem(path, index);
                            } else {
                              removeListItem(`${itemPath}.children`, removeIndex);
                            }
                          }))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : block.type === 'container' ? (
                  <div className="psm-block-container-preview">
                    <div className="psm-block-container-topbar">
                      <span className="psm-item-drag-handle psm-block-container-drag" title="Drag to reorder" draggable onDragStart={dragHandlers.onDragStart(index)} onDragEnd={dragHandlers.onDragEnd}>⠿</span>
                      <span className="psm-block-container-label">Container</span>
                      <BlockAddMenu onAdd={(item) => addObjectListItem(`${itemPath}.children`, item)} maxColumns={GROUP_MAX_COLUMNS[block.width || 'full']} />
                      <div className="psm-container-controls-right">
                        <ContainerBackgroundDropdown value={block.background} onChange={(v) => updateObjectListItem(path, index, { background: v })} />
                        <ContainerBorderDropdown
                          borderStyle={block.borderStyle}
                          borderColor={block.borderColor}
                          onStyleChange={(v) => updateObjectListItem(path, index, { borderStyle: v })}
                          onColorChange={(v) => updateObjectListItem(path, index, { borderColor: v })}
                        />
                        <SizeDropdown value={block.width} onChange={(w) => updateObjectListItem(path, index, { width: w })} />
                      </div>
                      <button type="button" className="psm-block-container-remove" title="Remove container" onClick={() => removeListItem(path, index)}>×</button>
                    </div>
                    {renderBlockList(`${itemPath}.children`, depth + 1, { hideTrailingAdd: true, maxColumns: GROUP_MAX_COLUMNS[block.width || 'full'] })}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: block.align || 'left',
                      lineHeight: block.spacing || undefined,
                      background: block.bgColor || undefined,
                      padding: block.bgColor ? '14px 16px' : undefined,
                      borderRadius: block.bgColor ? '10px' : undefined,
                    }}
                  >
                    <RichTextField
                      className="psm-p-edit"
                      value={block.html}
                      placeholder="Text"
                      onChange={(v) => updateObjectListItem(path, index, { html: v })}
                      align={block.align}
                      onAlignChange={(a) => updateObjectListItem(path, index, { align: a })}
                      spacing={block.spacing}
                      onSpacingChange={(s) => updateObjectListItem(path, index, { spacing: s })}
                      bgColor={block.bgColor}
                      onBgColorChange={(c) => updateObjectListItem(path, index, { bgColor: c })}
                      linkValue={block.to}
                      onLinkChange={(v) => updateObjectListItem(path, index, { to: v })}
                    />
                  </div>
                )}
                {block.type === 'gap' && (
                  <div className="psm-block-width-row">
                    <span className="psm-block-width-label">Size</span>
                    {['small', 'medium', 'large'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`psm-block-width-btn${(block.size || 'medium') === s ? ' active' : ''}`}
                        title={s === 'small' ? 'Small' : s === 'medium' ? 'Medium' : 'Large'}
                        onClick={() => updateObjectListItem(path, index, { size: s })}
                      >
                        {s === 'small' ? 'S' : s === 'medium' ? 'M' : 'L'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Containers and Groups carry their own remove control in the topbar above
                  (flush outside the box, alongside drag/width controls) instead of this
                  generic one, which would otherwise overlap their actual content — for
                  Groups specifically, this stray extra button was what the per-item red ×s
                  looked like they were clicking, since it removed the whole group instead of
                  just one item. */}
              {!isContainer && !isGroup && (
                <button type="button" className="psm-editable-remove-x psm-block-remove-x" title="Remove block" onClick={() => removeListItem(path, index)}>×</button>
              )}
            </div>
          );
        })}
        {!hideTrailingAdd && (
        <BlockAddMenu
          onAdd={(item) => {
            if (depth === 0) {
              addObjectListItem(path, { type: 'container', children: [item], width: 'full' });
            } else {
              addObjectListItem(path, item);
            }
          }}
        />
        )}
      </div>
    );
  };

  const customSections = getList('customSections');
  const addCustomSection = () => {
    const id = `cs-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    updateField('customSections', [...getList('customSections'), { id, children: [], background: '' }]);
  };

  return (
    <div className={branding?.wrapperEnabled === false ? 'public-site-no-wrapper' : undefined} style={backgroundColor ? { background: backgroundColor } : undefined}>
    <PublicSiteHeader branding={branding} isSignedIn interactive={false} navEditor={navEditor} storeName={storeName || null} />
    <div ref={canvasScaleWrapRef} className="psm-canvas-scale-wrap">
    <div ref={canvasRef} className="psm-wysiwyg-canvas psm-test-page-canvas" style={backgroundColor ? { background: backgroundColor } : undefined}>
      {customSections.length === 0 ? (
        <>
          <div className="psm-add-section-row psm-add-section-row-top">
            <button type="button" className="psm-add-ghost-pill psm-add-section-btn" onClick={addCustomSection}>+ Add Section</button>
          </div>
          <div className="psm-test-page-empty">This page is empty. Click "+ Add Section" above to start building.</div>
        </>
      ) : (
        <>
          <div className="psm-home-sections-flow">
            {customSections.map((cs, csIndex) => {
              const csPath = `customSections.${csIndex}`;
              return (
                <section
                  key={cs.id}
                  className="psm-custom-section psm-editable-section"
                >
                  <div className="psm-custom-section-toolbar">
                                        <ContainerBackgroundDropdown value={cs.background} onChange={(v) => updateObjectListItem('customSections', csIndex, { background: v })} />
                                        <ContainerBorderDropdown
                                          borderStyle={cs.borderStyle}
                                          borderColor={cs.borderColor}
                                          onStyleChange={(v) => updateObjectListItem('customSections', csIndex, { borderStyle: v })}
                                          onColorChange={(v) => updateObjectListItem('customSections', csIndex, { borderColor: v })}
                                        />
                                        <button type="button" className="psm-custom-section-remove" onClick={() => removeListItem('customSections', csIndex)}>Remove section</button>
                  </div>
                  <div className="psm-custom-section-inner" style={{ ...getContainerStyle(cs, 'none'), background: getSectionBackground(cs, branding) }}>
                    {renderBlockList(`${csPath}.children`, 0)}
                  </div>
                </section>
              );
            })}
          </div>
          <div className="psm-add-section-row">
            <button type="button" className="psm-add-ghost-pill psm-add-section-btn" onClick={addCustomSection}>+ Add Section</button>
          </div>
        </>
      )}
    </div>
    </div>
    </div>
  );
};

// A single rounded bar — circular swatch, hex text, reset — instead of a separate boxy
// swatch + plain text input + secondary button sitting side by side. Shared by every
// branding colour field so they all look and behave the same.
const ColorField = ({ label, value, defaultHex, placeholder, onChange }) => {
  const resolvedHex = /^#[0-9a-f]{6}$/i.test(value || '') ? value : defaultHex;
  return (
    <label className="psm-field">
      <span>{label}</span>
      <div className="psm-color-field">
        <input
          type="color"
          className="psm-color-field-swatch"
          value={resolvedHex}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          type="text"
          className="psm-color-field-hex"
          placeholder={placeholder}
          value={value || ''}
          onFocus={(e) => {
            // Empty field shows just the placeholder hint — focusing it should show the
            // actual hex it'll use (the default) pre-selected, ready to type over, instead
            // of staying blank until you guess at a value. The value prop updates on the
            // next render, so the select() has to wait a tick to act on the new text.
            if (!value) {
              onChange(defaultHex);
              requestAnimationFrame(() => e.target.select());
            } else {
              e.target.select();
            }
          }}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" className="psm-color-field-reset" title="No colour" disabled={!value} onClick={() => onChange('')}>
          No colour
        </button>
      </div>
    </label>
  );
};

// A link opened with ?fullscreen=1&page=<key> (see the "Page Settings" sidebar item) lands
// straight on that page with the admin nav already hidden (see chromeHidden in
// PlatformAdminSite.js) — a real, unconstrained browser window for previewing/editing,
// instead of one that's still narrower than a live visitor's.
const getInitialPageFromUrl = () => {
  const key = new URLSearchParams(window.location.search).get('page');
  return key && PAGE_LABELS[key] ? key : 'branding';
};

const StoreSiteManager = ({ restaurantId, restaurantSlug, restaurantName }) => {
  const [contentMap, setContentMap] = useState(() => buildStoreSiteContentMap());
  const [selectedPage] = useState(getInitialPageFromUrl);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 3000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(''), 3000);
    return () => window.clearTimeout(timer);
  }, [error]);
  const [brandingDraft, setBrandingDraft] = useState(() => cloneValue(STORE_SITE_DEFAULTS.branding));
  const [pageDraft, setPageDraft] = useState(() => cloneValue(STORE_SITE_DEFAULTS.home));
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Undo/redo history for the page currently being edited. Edits are coalesced into
  // one history entry per short burst of typing, instead of one entry per keystroke.
  const [pageHistory, setPageHistory] = useState([]);
  const [pageFuture, setPageFuture] = useState([]);
  const pendingHistoryBaseRef = useRef(null);
  const historyTimerRef = useRef(null);
  const HISTORY_DEBOUNCE_MS = 700;
  const MAX_HISTORY = 50;

  const resetHistory = useCallback(() => {
    if (historyTimerRef.current) { window.clearTimeout(historyTimerRef.current); historyTimerRef.current = null; }
    pendingHistoryBaseRef.current = null;
    setPageHistory([]);
    setPageFuture([]);
  }, []);

  const handlePageDraftChange = useCallback((updater) => {
    setPageDraft((current) => {
      if (pendingHistoryBaseRef.current === null) {
        pendingHistoryBaseRef.current = current;
      }
      return typeof updater === 'function' ? updater(current) : updater;
    });
    setPageFuture([]);
    if (historyTimerRef.current) window.clearTimeout(historyTimerRef.current);
    historyTimerRef.current = window.setTimeout(() => {
      historyTimerRef.current = null;
      setPageHistory((prevHistory) => {
        if (pendingHistoryBaseRef.current === null) return prevHistory;
        const next = [...prevHistory, pendingHistoryBaseRef.current].slice(-MAX_HISTORY);
        pendingHistoryBaseRef.current = null;
        return next;
      });
    }, HISTORY_DEBOUNCE_MS);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyTimerRef.current) { window.clearTimeout(historyTimerRef.current); historyTimerRef.current = null; }
    setPageHistory((prevHistory) => {
      const withFlushed = pendingHistoryBaseRef.current !== null ? [...prevHistory, pendingHistoryBaseRef.current] : prevHistory;
      pendingHistoryBaseRef.current = null;
      if (withFlushed.length === 0) return withFlushed;
      const previous = withFlushed[withFlushed.length - 1];
      setPageFuture((prevFuture) => [pageDraft, ...prevFuture].slice(0, MAX_HISTORY));
      setPageDraft(previous);
      return withFlushed.slice(0, -1);
    });
  }, [pageDraft]);

  const handleRedo = useCallback(() => {
    setPageFuture((prevFuture) => {
      if (prevFuture.length === 0) return prevFuture;
      const next = prevFuture[0];
      setPageHistory((prevHistory) => [...prevHistory, pageDraft].slice(-MAX_HISTORY));
      setPageDraft(next);
      return prevFuture.slice(1);
    });
  }, [pageDraft]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const rows = await fetchStoreSiteContent(restaurantId);
        if (active) { setContentMap(buildStoreSiteContentMap(rows)); setError(''); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load site content.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageDraft(cloneValue(contentMap[selectedPage] || STORE_SITE_DEFAULTS[selectedPage] || { customSections: [] }));
    resetHistory();
  }, [contentMap, selectedPage, resetHistory]);

  // Snapshot of what was live when this page was opened in the editor — captured once
  // per page (and once loading finishes), not re-captured after every Save. "Reset"
  // reverts to this, undoing any saves made during this session, not just unsaved edits.
  const sessionSnapshotRef = useRef(null);
  useEffect(() => {
    if (!loading) {
      sessionSnapshotRef.current = cloneValue(contentMap[selectedPage] || STORE_SITE_DEFAULTS[selectedPage] || { customSections: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage, loading]);

  useEffect(() => {
    const branding = contentMap.branding || STORE_SITE_DEFAULTS.branding;
    setBrandingDraft({ ...cloneValue(branding), navItems: normalizeBrandingNavItems(branding) });
  }, [contentMap]);

  const updateBrandingDraft = useCallback((updater) => {
    setBrandingDraft((current) => (typeof updater === 'function' ? updater(current) : updater));
    setNotice('');
    setError('');
  }, []);

  // Switching pages in the fullscreen editor — by clicking a link in the page header's own
  // nav — is a full reload of this same URL with a new ?page=, not in-place state swapping,
  // since fullscreen editing is meant to behave like a real, standalone window for one page
  // at a time. Clicking a custom (non-page) link is a no-op here — PAGE_LABELS only has
  // entries for the actual editable pages.
  const goToEditorPage = useCallback((pageKey) => {
    if (pageKey === 'order') {
      window.location.href = `/dashboard/stores/${restaurantId}/products`;
      return;
    }
    if (!PAGE_LABELS[pageKey] || pageKey === selectedPage) return;
    window.location.href = `/dashboard/stores/${restaurantId}/website?fullscreen=1&page=${pageKey}`;
  }, [selectedPage, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nav items are now managed entirely inside the fullscreen editor's own header nav (see
  // PublicSiteHeader's navEditor prop) — drag to reorder, Remove (top, confirm required),
  // Rename/Hide (bottom). There's no separate Navigation Links form in General Settings
  // anymore. Drag state lives here rather than inside PublicSiteHeader since that component
  // is also rendered un-editable on the live site.
  const [navDragIndex, setNavDragIndex] = useState(null);
  const [navOverIndex, setNavOverIndex] = useState(null);

  const reorderNavItem = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    updateBrandingDraft((current) => {
      const items = [...(current.navItems || [])];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);
      return { ...current, navItems: items };
    });
  }, [updateBrandingDraft]);

  const toggleNavItemHidden = useCallback((index) => {
    updateBrandingDraft((current) => {
      const items = [...(current.navItems || [])];
      if (!items[index]) return current;
      items[index] = { ...items[index], hidden: !items[index].hidden };
      return { ...current, navItems: items };
    });
  }, [updateBrandingDraft]);

  // At least one nav item must always remain — with zero items there'd be nothing left to
  // click to get this control back. Hiding (reversible, no confirmation) is the right tool
  // for "I don't want this visible right now"; Remove is for "this link shouldn't exist."
  const removeNavItem = useCallback((index) => {
    const items = brandingDraft.navItems || [];
    if (items.length <= 1) {
      window.alert('At least one nav item must remain — hide it instead of removing it.');
      return;
    }
    const target = items[index];
    if (!target) return;
    if (!window.confirm(`Remove "${target.label || 'this item'}" from the navigation? This can't be undone — you'd need to add it back with "+".`)) return;
    updateBrandingDraft((current) => ({
      ...current,
      navItems: (current.navItems || []).filter((_, i) => i !== index),
    }));
  }, [brandingDraft, updateBrandingDraft]);

  // A built-in page's `to` is what ties the nav item back to the page it switches the editor
  // to on click (see goToEditorPage) — only its label is renameable. Custom links aren't tied
  // to any page, so both their label and target get prompted.
  const renameNavItem = useCallback((index) => {
    const items = brandingDraft.navItems || [];
    const item = items[index];
    if (!item) return;
    const isBuiltInPage = Boolean(PAGE_LABELS[pathFromNavItem(item)]) && pathFromNavItem(item) !== 'branding';
    const label = window.prompt('Nav item label', item.label || '');
    if (label === null || !label.trim()) return;
    let to = item.to;
    if (!isBuiltInPage) {
      const nextTo = window.prompt('Link target (e.g. /pricing or https://...)', item.to || '');
      if (nextTo === null || !nextTo.trim()) return;
      to = nextTo.trim();
    }
    updateBrandingDraft((current) => {
      const nextItems = [...(current.navItems || [])];
      nextItems[index] = { ...nextItems[index], label: label.trim(), to };
      return { ...current, navItems: nextItems };
    });
  }, [brandingDraft, updateBrandingDraft]);

  const addNavItem = useCallback(() => {
    const label = window.prompt('Label for the new nav link?', 'New link');
    if (label === null || !label.trim()) return;
    const to = window.prompt('Link target (e.g. /pricing or https://...)', '/');
    if (to === null || !to.trim()) return;
    updateBrandingDraft((current) => ({
      ...current,
      navItems: [...(current.navItems || []), { label: label.trim(), to: to.trim() }],
    }));
  }, [updateBrandingDraft]);

  const navEditor = useMemo(() => ({
    dragIndex: navDragIndex,
    overIndex: navOverIndex,
    currentPage: selectedPage,
    protectedTos: ['/order'],
    onDragStart: setNavDragIndex,
    onDragOver: (index) => setNavOverIndex((current) => (current === index ? current : index)),
    onDrop: (index) => {
      if (navDragIndex !== null && navDragIndex !== index) reorderNavItem(navDragIndex, index);
      setNavDragIndex(null);
      setNavOverIndex(null);
    },
    onDragEnd: () => { setNavDragIndex(null); setNavOverIndex(null); },
    onRemove: removeNavItem,
    onToggleHidden: toggleNavItemHidden,
    onRename: renameNavItem,
    onAdd: addNavItem,
    onNavigate: goToEditorPage,
  }), [navDragIndex, navOverIndex, selectedPage, reorderNavItem, removeNavItem, toggleNavItemHidden, renameNavItem, addNavItem, goToEditorPage]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploadingLogo(true);
      const uploaded = await uploadStoreSiteAsset(restaurantId, file, 'branding');
      setBrandingDraft((current) => ({ ...current, logoImageUrl: uploaded.url }));
      setNotice('Logo uploaded. Save changes to publish it.');
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to upload logo.');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const handlePageAssetUpload = async (event, path, folder) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const uploaded = await uploadStoreSiteAsset(restaurantId, file, folder || selectedPage);
      handlePageDraftChange((current) => setByPath(current, path, uploaded.url));
      setNotice('Image uploaded. Save changes to publish it.');
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to upload image.');
    } finally {
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    try {
      const brandingValue = brandingDraft;
      const nextValue = selectedPage === 'branding' ? brandingValue : pageDraft;

      setSaving(true);
      await upsertStoreSiteContent(restaurantId, selectedPage, nextValue);
      if (selectedPage !== 'branding') {
        await upsertStoreSiteContent(restaurantId, 'branding', brandingValue);
      }
      try {
        const freshRows = await fetchStoreSiteContent(restaurantId);
        localStorage.setItem(`ss_store_site_${restaurantId}_v1`, JSON.stringify(freshRows));
      } catch {}
      setContentMap((current) => ({
        ...current,
        branding: deepMergeStoreSiteContent(STORE_SITE_DEFAULTS.branding, brandingValue),
        [selectedPage]: deepMergeStoreSiteContent(STORE_SITE_DEFAULTS[selectedPage] || { customSections: [] }, nextValue),
      }));
      setNotice(`${PAGE_LABELS[selectedPage]} saved.`);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to save site content.');
      setNotice('');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Revert ${PAGE_LABELS[selectedPage]} back to how it was before your changes this session?`)) return;
    const snapshot = sessionSnapshotRef.current || STORE_SITE_DEFAULTS[selectedPage] || { customSections: [] };
    try {
      setSaving(true);
      await upsertStoreSiteContent(restaurantId, selectedPage, snapshot);
      try {
        const freshRows = await fetchStoreSiteContent(restaurantId);
        localStorage.setItem(`ss_store_site_${restaurantId}_v1`, JSON.stringify(freshRows));
      } catch {}
      if (selectedPage === 'branding') {
        setBrandingDraft(cloneValue(snapshot));
      }
      setContentMap((current) => ({ ...current, [selectedPage]: deepMergeStoreSiteContent(STORE_SITE_DEFAULTS[selectedPage] || { customSections: [] }, snapshot) }));
      setNotice(`${PAGE_LABELS[selectedPage]} reverted to before this session's changes.`);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to revert page content.');
      setNotice('');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="platform-admin-state">Loading website editor…</div>;

  const pageDescription = selectedPage === 'branding'
    ? 'Upload your store logo and control website header settings here.'
    : 'Build this page from scratch using sections, containers, and items — this looks exactly like the live page.';

  const storeBaseUrl = restaurantSlug ? `https://${restaurantSlug}.servsolo.com` : '';
  const previewPath = storeBaseUrl + (selectedPage === 'branding' || selectedPage === 'home' ? '' : `/${selectedPage}`);
  // ?fullscreen=1 (opened via "Page Settings" in the sidebar) — a genuinely unconstrained
  // editing window that looks like the live page, not the admin tool wrapped around it: no
  // "Public WebSite" heading, no editor title/description, no card border/padding. Just the
  // page header (non-interactive) flush against the editable canvas, the same as a real
  // visitor would see, plus a minimal page-switcher/Save/Undo/Redo row. This is now the only
  // way pages get edited — there's no embedded editing mode left inside the admin layout.
  const isFullscreen = new URLSearchParams(window.location.search).get('fullscreen') === '1';

  return (
    <section className={`platform-admin-section psm-shell${isFullscreen ? ' psm-shell-fullscreen' : ''}`}>
      {!isFullscreen && (
        <div className="platform-admin-section-header psm-header-row">
          <div>
            <h2>Store Website</h2>
            <p>Build and manage the public website for {restaurantName || 'this store'}.</p>
          </div>
        </div>
      )}

      <div className="psm-layout">
        <div className="psm-edit-col-full">
          {/* Editor card */}
          <div className={`psm-editor-card${isFullscreen ? ' psm-editor-card-fullscreen' : ''}`}>
            <div className={isFullscreen ? 'psm-editor-head psm-editor-head-fullscreen' : 'psm-editor-head'}>
              {!isFullscreen && (
                <div>
                  <strong>{PAGE_LABELS[selectedPage]}</strong>
                  <p>{pageDescription}</p>
                </div>
              )}
              <div className="psm-editor-head-actions">
                {selectedPage !== 'branding' && (
                  <>
                    <button type="button" className="psm-undo-redo-btn" title="Undo" disabled={pageHistory.length === 0} onClick={handleUndo}>↶ Undo</button>
                    <button type="button" className="psm-undo-redo-btn" title="Redo" disabled={pageFuture.length === 0} onClick={handleRedo}>↷ Redo</button>
                  </>
                )}
                <button type="button" className="psm-undo-redo-btn" title="Revert to before this session's changes" disabled={saving} onClick={handleReset}>Reset</button>
                <button type="button" className="psm-undo-redo-btn" title="Save changes" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
                {storeBaseUrl && (
                  <a className="psm-preview-link" href={previewPath} target="_blank" rel="noreferrer">
                    Open page ↗
                  </a>
                )}
              </div>
            </div>

            {notice && <div className="platform-admin-inline-note platform-admin-inline-note-success">{notice}</div>}
            {error && <div className="platform-admin-inline-note platform-admin-inline-note-error">{error}</div>}

            {selectedPage === 'branding' ? (
              <div className="psm-branding-form">
                <div className="psm-logo-upload-row">
                  <div className="psm-logo-preview-card">
                    {brandingDraft.logoImageUrl ? (
                      <img src={brandingDraft.logoImageUrl} alt={brandingDraft.logoAlt || 'Store logo preview'} className="psm-logo-preview" />
                    ) : (
                      <div className="psm-logo-preview-empty">No logo uploaded yet</div>
                    )}
                  </div>
                  <label className="platform-admin-header-btn secondary psm-upload-btn">
                    {uploadingLogo ? 'Uploading…' : 'Upload logo image'}
                    <input type="file" accept="image/*,.svg" onChange={handleLogoUpload} hidden />
                  </label>
                </div>
                <label className="psm-field">
                  <span>Logo image URL</span>
                  <input
                    type="url"
                    value={brandingDraft.logoImageUrl ?? ''}
                    placeholder="https://..."
                    onChange={(e) => setBrandingDraft((c) => ({ ...c, logoImageUrl: e.target.value }))}
                  />
                </label>
                {BRANDING_FIELDS.map((field) => (
                  <label key={field.key} className="psm-field">
                    <span>{field.label}</span>
                    <input
                      type={field.type}
                      value={brandingDraft[field.key] ?? ''}
                      placeholder={field.placeholder}
                      onChange={(e) => {
                        const v = field.type === 'number' ? Number(e.target.value || 0) : e.target.value;
                        setBrandingDraft((c) => ({ ...c, [field.key]: v }));
                      }}
                    />
                  </label>
                ))}
                <ColorField
                  label="Page background colour"
                  value={brandingDraft.backgroundColor}
                  defaultHex="#ffffff"
                  placeholder="Default white (leave blank)"
                  onChange={(v) => setBrandingDraft((c) => ({ ...c, backgroundColor: v }))}
                />
                <ColorField
                  label="Wrapper colour"
                  value={brandingDraft.wrapperColor}
                  defaultHex="#ffffff"
                  placeholder="Default white (leave blank)"
                  onChange={(v) => setBrandingDraft((c) => ({ ...c, wrapperColor: v }))}
                />
                <label className="psm-field psm-wrapper-toggle">
                  <input
                    type="checkbox"
                    checked={brandingDraft.wrapperEnabled !== false}
                    onChange={(e) => setBrandingDraft((c) => ({ ...c, wrapperEnabled: e.target.checked }))}
                  />
                  <span>
                    Add Wrapper?
                    <small>On: header and section cards use Wrapper colour as their own layer (today's look). Off: they go fully transparent — header, body, and (later) footer all extend as one continuous Page background colour, full width.</small>
                  </span>
                </label>
                <div className="platform-admin-inline-note">
                  Page background colour paints the page behind everything. Wrapper colour paints the header and the card that wraps your sections' content. Uploaded files are stored in Supabase Storage and published when you save.
                </div>
                <div className="platform-admin-inline-note">
                  Nav links (add, remove, reorder, hide, rename) are managed directly in the page header — open "Page Settings" to edit them there.
                </div>
              </div>
            ) : (
              // Every page (Home included) uses the same blank-canvas Container/Group/
              // Section editor — there's no more dedicated hero editor or fixed-field
              // template form for any page.
              <PageEditor value={pageDraft} onChange={handlePageDraftChange} onUpload={handlePageAssetUpload} branding={brandingDraft} backgroundColor={brandingDraft.backgroundColor} navEditor={isFullscreen ? navEditor : undefined} storeName={restaurantName || null} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreSiteManager;
